import { appendIf } from '@btime/helpers'
import { useTranslation } from 'react-i18next'
import { useIsOwner, useSessionPermission } from '@btime/react-session'

import {
  useCloneServiceOrderAction,
  useCreateServiceOrderChildAction,
  useEditServiceOrderAction,
  useEditServiceOrderUserAction,
  useEditServiceOrdersAction,
  useEditServiceOrdersStatusAction,
  useExportSelectedServiceOrdersAction,
  useMailPublicServiceOrderExecutionLinkAction,
  useMailPublicServiceOrdersExecutionLinkAction,
  useRemoveServiceOrderAction,
  useRemoveServiceOrdersAction,
  useServiceOrderDetailsAction,
  useServiceOrderDocGeneratorAction,
  useServiceOrderExecutionAction,
  useServiceOrderLogsAction,
  useServiceOrderResumeAction,
  useServiceOrdersLogsAction
} from './actions'
import { useBulkExecuteServiceOrdersAction } from './bulkExecution'
import { useColors } from '../../../packages/kiti/components/theme/_hooks/colors'
import { useIsInModal } from '../../../components/modals/ModalsContext'
import { useServiceOrderPermissions } from '../../permissions/serviceOrders/serviceOrderPermissions'

const useSingleActions = (item, opts = {}) => {
  const { t } = useTranslation('common')
  const hide = opts.hide || []
  const colors = useColors()

  const details = useServiceOrderDetailsAction(item)
  const logs = useServiceOrderLogsAction(item)
  const edit = useEditServiceOrderAction(item)
  const createChild = useCreateServiceOrderChildAction(item)
  const mailPublicExecution = useMailPublicServiceOrderExecutionLinkAction(item)
  const execution = useServiceOrderExecutionAction(item, useIsInModal())
  const resume = useServiceOrderResumeAction(item)
  const editUser = useEditServiceOrderUserAction(item)
  const editStatus = useEditServiceOrdersStatusAction([item?.id])
  const clone = useCloneServiceOrderAction(item)
  const generateDoc = useServiceOrderDocGeneratorAction(item)
  const remove = useRemoveServiceOrderAction(item)
  const exportSelectedServiceOrder = useExportSelectedServiceOrdersAction([item?.id])

  return [
    ...appendIf(!hide.includes('seeDetails'), {
      value: t('seeDetails'),
      icon: 'BsSearch',
      onClick: details.click
    }),

    ...appendIf(generateDoc.hasPermission, {
      value: t('generateCustomDocument'),
      icon: 'BsFileCheck',
      onClick: generateDoc.click
    }),

    ...appendIf(logs.hasPermission, {
      value: t('logs'),
      icon: 'RiTimeLine',
      onClick: logs.click,
      to: logs.link
    }),

    ...appendIf(createChild.hasPermission, {
      value: t('createChildActivity'),
      icon: 'BsFolderPlus',
      onClick: createChild.click
    }),

    ...appendIf(mailPublicExecution.hasPermission, {
      value: t('sendPublicLink'),
      icon: 'BsFillReplyFill',
      onClick: mailPublicExecution.click
    }),

    ...appendIf(execution.hasPermission, {
      value: t('execute'),
      icon: 'BsCheckCircle',
      onClick: execution.click,
      to: execution.link
    }),

    ...appendIf(!hide.includes('resume') && resume.hasPermission, {
      value: t('resume'),
      icon: 'BsFileRichtext',
      onClick: resume.click,
      to: resume.link
    }),

    ...appendIf(editUser.hasPermission, {
      value: t('changeUser'),
      icon: 'BsPerson',
      onClick: editUser.click
    }),

    ...appendIf(!hide.includes('clone') && clone.hasPermission, {
      value: t('clone'),
      icon: 'BsLayers',
      onClick: clone.click
    }),

    ...appendIf(editStatus.hasPermission, {
      value: t('changeStatus'),
      icon: 'BsFillTagsFill',
      onClick: editStatus.click
    }),

    ...appendIf(edit.hasPermission, {
      value: t('edit'),
      icon: 'BsPencil',
      onClick: edit.click
    }),

    ...appendIf(exportSelectedServiceOrder.hasPermission, {
      value: t('export'),
      icon: 'RiDownloadCloud2Line',
      onClick: exportSelectedServiceOrder.click
    }),

    ...appendIf(!hide.includes('remove') && remove.hasPermission, {
      value: t('remove'),
      color: colors.red,
      icon: 'BsTrash',
      onClick: remove.click
    })
  ]
}

const useMultiActions = (ids, statuses, opts = {}) => {
  const { t } = useTranslation('common')
  const hide = opts.hide || []
  const colors = useColors()

  const logs = useServiceOrdersLogsAction(ids)
  const mailPublicExecution = useMailPublicServiceOrdersExecutionLinkAction(ids)
  const bulkExecute = useBulkExecuteServiceOrdersAction(ids)
  const edit = useEditServiceOrdersAction(ids, statuses)
  const editStatus = useEditServiceOrdersStatusAction(ids)
  const remove = useRemoveServiceOrdersAction(ids)

  const exportSelectedServiceOrder = useExportSelectedServiceOrdersAction(ids)

  return [
    ...appendIf(logs.hasPermission, {
      value: t('logs'),
      icon: 'RiTimeLine',
      onClick: logs.click,
      to: logs.link
    }),

    ...appendIf(mailPublicExecution.hasPermission, {
      value: t('sendPublicLinks'),
      icon: 'BsFillReplyFill',
      onClick: mailPublicExecution.click
    }),

    ...appendIf(!hide.includes('bulkExecute') && bulkExecute.hasPermission, {
      value: t('executeInBulk'),
      icon: 'BsCheckCircle',
      onClick: bulkExecute.click
    }),

    ...appendIf(editStatus.hasPermission, {
      value: t('changeStatus'),
      icon: 'BsFillTagsFill',
      onClick: editStatus.click
    }),

    ...appendIf(edit.hasPermission, {
      value: t('edit'),
      icon: 'BsPencil',
      onClick: edit.click
    }),

    ...appendIf(exportSelectedServiceOrder.hasPermission, {
      value: t('export'),
      icon: 'RiDownloadCloud2Line',
      onClick: exportSelectedServiceOrder.click
    }),

    ...appendIf(remove.hasPermission, {
      value: t('remove'),
      color: colors.red,
      icon: 'BsTrash',
      onClick: remove.click
    })
  ]
}

export const useServiceOrdersActionsList = (item, ids, statuses = [], opts = {}) => {
  const hide = opts.hide || []
  const { canEdit, canDelete } = useServiceOrderPermissions(item)
  const hasPublicExecutionPermission = useSessionPermission('executePublicServiceOrder')
  const isOwner = useIsOwner()

  const singleOptions = useSingleActions(item, opts)
  const multiOptions = useMultiActions(ids, statuses, opts)

  if (!!ids && ids.length > 1) return multiOptions
  if (!!item && !!item?.id) return singleOptions

  return multiOptions
}
